import React, { createContext, useContext, useState, useCallback } from 'react';

const ChatbotContext = createContext();

export const useChatbot = () => useContext(ChatbotContext);

export const ChatbotProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [hasShownWelcome, setHasShownWelcome] = useState(false);
    const [showOptions, setShowOptions] = useState(false);

    const SYSTEM_PROMPT = `You are a helpful assistant for the SaveMatic compound interest calculator. 
    Your role is to help users understand and use the calculator effectively.
    
    The calculator can handle:
    1. Regular savings calculations
    2. House purchase calculations
    3. House purchase calculations with mortgage calculations
    
    For regular savings, users need to input under "Investment" tab:
    - Initial investment
    - Monthly contribution
    - Interest rate
    - Investment period (years)
    
    For house purchase, users need to input under "House" tab:
    - House price
    - House price increase rate
    - All regular savings inputs
    
    For mortgage, users need to input under "Mortgage" tab:
    - All regular savings inputs
    - All house purchase inputs
    - Mortgage amount that user would like to take
    
    Do Not say "In the context of the SaveMatic" or "In the context of the calculator".
    Answer in a way that is easy to understand. Answer in few sentences.
    Always be polite and helpful. If users ask about topics not related to the calculator, 
    politely inform them that you can only help with calculator-related questions.`;

    const sendMessage = useCallback(async (userMessage) => {
        setIsLoading(true);
        const newUserMessage = { role: 'user', content: userMessage };
        setMessages(prev => [...prev, newUserMessage]);

        try {
            const response = await fetch('https://api-inference.huggingface.co/models/mistralai/Mistral-7B-Instruct-v0.2', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer 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`
                },
                body: JSON.stringify({
                    inputs: `<s>[INST] ${SYSTEM_PROMPT} \n\nPrevious conversation:\n${messages.map(m => `${m.role}: ${m.content}`).join('\n')}\n\nUser: ${userMessage} [/INST]`,
                    parameters: {
                        max_new_tokens: 500,
                        temperature: 0.7,
                        top_p: 0.95,
                        repetition_penalty: 1.1
                    }
                })
            });

            const data = await response.json();
            
            if (data.error) {
                throw new Error(data.error);
            }

            const botMessage = data[0].generated_text.split('[/INST]').pop().trim();
            
            setMessages(prev => [...prev, { role: 'assistant', content: botMessage }]);
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: 'I apologize, but I encountered an error. Please try again later.' 
            }]);
        } finally {
            setIsLoading(false);
        }
    }, [messages]);

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen && !hasShownWelcome) {
            setHasShownWelcome(true);
            setMessages([{ 
                role: 'assistant', 
                content: "Welcome! I can help you understand how this calculator works."
            }]);
            setShowOptions(true);
        }
    };

    const handleOptionClick = (option) => {
        setShowOptions(false);
        sendMessage(option);
    };

    const clearChat = useCallback(() => {
        setMessages([]);
        setHasShownWelcome(false);
        setShowOptions(false);
    }, []);

    return (
        <ChatbotContext.Provider value={{
            messages,
            setMessages,
            isLoading,
            isOpen,
            hasShownWelcome,
            showOptions,
            setShowOptions,
            sendMessage,
            toggleChat,
            clearChat,
            handleOptionClick,
            setHasShownWelcome
        }}>
            {children}
        </ChatbotContext.Provider>
    );
}; 